import React from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import {
  useGetCartQuery,
  useRemoveItemFromCartMutation,
  useUpdateCartItemMutation,
} from "../../../../cart/api/cartApi";
import RTKQErrorCard from "../../../../../common/components/RTKQErrorCard";
import type { RTKQueryError } from "../../../../../common/components/RTKQErrorCard";
// Import new view components
import MiniCartItemsList from "./MiniCartItemsList";
import MiniCartTotals from "./MiniCartTotals";
import MiniCartEmptyCartMessage from "./MiniCartEmptyCartMessage";
// Import type for shipping rate
import type { TransformedCartShippingRate } from "../../../../cart/types/cartApiTypes";

const CartPanel: React.FC = () => {
  const { t } = useTranslation();
  const currencySymbol = window.themeVariables?.currencySymbol || "₺";
  const {
    data: cart,
    isLoading,
    isError,
    error,
  } = useGetCartQuery(undefined, {
    refetchOnMountOrArgChange: true, // Refetch when the panel mounts (opens)
  });
  const [removeItemFromCart, { isLoading: isRemoving }] =
    useRemoveItemFromCartMutation();
  const [updateCartItem, { isLoading: isUpdating }] = // Get update mutation and state
    useUpdateCartItemMutation();

  const isProcessing = isRemoving || isUpdating; // Combined processing state

  const handleRemoveItem = (key: string) => {
    if (isProcessing) return;
    removeItemFromCart({ key });
  };

  // Add quantity change handler
  const handleQuantityChange = (key: string, newQuantity: number) => {
    if (isProcessing) return; // Prevent changes while processing

    const item = cart?.items.find((i) => i.key === key);
    if (!item) return;

    const min = item.quantityLimits.minimum;
    const max = item.quantityLimits.maximum;
    const step = item.quantityLimits.multiple_of;

    // Clamp and adjust quantity based on limits and step
    let clampedQuantity = Math.max(min, newQuantity);
    if (max > 0) {
      clampedQuantity = Math.min(max, clampedQuantity);
    }
    const adjustedQuantity = Math.floor(clampedQuantity / step) * step;
    const finalQuantity = Math.max(min, adjustedQuantity);

    // Only update if the quantity is valid and different
    if (finalQuantity !== item.quantity && finalQuantity >= min) {
      updateCartItem({ key, quantity: finalQuantity });
    }
  };

  // --- Render Logic ---

  let content;
  let isFreeShipping = false; // Default to false

  if (isLoading) {
    content = (
      <div className="cart-panel__loading">
        {/* Basic Skeleton for loading state - Keep similar structure */}
        {[...Array(2)].map((_, index) => (
          <div key={index} className="cart-panel-item-skeleton">
            <Skeleton circle height={50} width={50} />
            <div className="cart-panel-item-skeleton__details">
              <Skeleton height={16} width="70%" />
              <Skeleton height={14} width="40%" />
            </div>
            <Skeleton height={20} width={20} />
          </div>
        ))}
      </div>
    );
  } else if (isError) {
    content = (
      <RTKQErrorCard
        error={error as RTKQueryError | null}
        title={t("cart.errors.loadFailed", "Failed to load cart")}
        compact // Optional: Add a compact style for the panel
      />
    );
  } else if (!cart || cart.itemsCount === 0) {
    // Use MiniCartEmptyCartMessage
    content = <MiniCartEmptyCartMessage />;
  } else {
    // Find selected shipping rate and check if it's free
    const selectedShippingRate: TransformedCartShippingRate | undefined =
      cart.shippingRates
        .flatMap((pkg) => pkg.shippingRates)
        .find((rate) => rate.selected);
    isFreeShipping = selectedShippingRate?.methodId === "free_shipping";

    // Use MiniCartItemsList
    content = (
      <MiniCartItemsList
        items={cart.items}
        currencySymbol={currencySymbol}
        onRemoveItem={handleRemoveItem}
        onQuantityChange={handleQuantityChange} // Pass handler
        isProcessing={isProcessing} // Pass combined state
      />
    );
  }

  return (
    <div className="cart-panel">
      {/* Main content area (list, empty message, loading, error) */}
      <div className="cart-panel__content">{content}</div>

      {/* Footer area (totals and actions) */}
      <div className="cart-panel__footer">
        {/* Use MiniCartTotals - Render only if cart data is available */}
        {cart && (
          <MiniCartTotals
            totals={cart.totals}
            coupons={cart.coupons}
            currencySymbol={currencySymbol}
            itemCount={cart.itemsCount}
            needsShipping={cart.needsShipping} // Pass needsShipping
            hasCalculatedShipping={cart.hasCalculatedShipping} // Pass hasCalculatedShipping
            isFreeShipping={isFreeShipping} // Pass the flag
          />
        )}
        {/* Render skeleton for totals during initial load */}
        {isLoading && (
          <div className="mini-cart-totals-skeleton">
            <Skeleton
              height={20}
              width="80%"
              style={{ marginBottom: "0.5rem" }} // Reduced margin
            />
            <Skeleton
              height={20}
              width="70%"
              style={{ marginBottom: "1rem" }} // Margin before buttons
            />
            <Skeleton height={40} width="100%" />
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPanel;
